/**
 * The single Axios instance every API module shares.
 *
 * Responsibilities:
 *  - Attach the Bearer access token and send cookies (`withCredentials`).
 *  - Transparently refresh an expired access token on a 401 using the httpOnly
 *    refresh cookie, then replay the original request. Concurrent 401s share a
 *    single in-flight refresh (single-flight) to avoid a refresh stampede.
 *  - Normalise every failure into a typed `ApiRequestError`.
 */
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { env } from "../config/env";
import { tokenStore } from "./tokenStore";
import { ApiEnvelope, ApiErrorBody, ApiRequestError } from "../types/api";

/** Requests that must never trigger an automatic refresh-and-retry loop. */
const AUTH_FREE_PATHS = ["/auth/login", "/auth/register", "/auth/refresh"];

/** Called when the session is irrecoverable (refresh failed). Wired up by the auth layer. */
let onSessionExpired: (() => void) | null = null;
export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: true,
  timeout: 30_000,
  headers: { Accept: "application/json" },
});

apiClient.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) {
    const headers = AxiosHeaders.from(config.headers);
    headers.set("Authorization", `Bearer ${token}`);
    config.headers = headers;
  }
  return config;
});

// ── Silent token refresh (single-flight) ─────────────────────────────

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

let refreshPromise: Promise<string | null> | null = null;

/** Hit `/auth/refresh` (uses the refresh cookie) and persist the new access token. */
async function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post<ApiEnvelope<{ accessToken: string }>>(
        `${env.apiBaseUrl}/auth/refresh`,
        {},
        { withCredentials: true },
      )
      .then((res) => {
        const token = res.data?.data?.accessToken ?? null;
        tokenStore.set(token);
        return token;
      })
      .catch(() => {
        tokenStore.clear();
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const config = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const url = config?.url ?? "";
    const isAuthFree = AUTH_FREE_PATHS.some((p) => url.includes(p));

    if (status === 401 && config && !config._retried && !isAuthFree) {
      config._retried = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        const headers = AxiosHeaders.from(config.headers);
        headers.set("Authorization", `Bearer ${newToken}`);
        config.headers = headers;
        return apiClient(config);
      }
      // Refresh failed — session is over.
      onSessionExpired?.();
    }

    return Promise.reject(toApiError(error));
  },
);

/** Convert any Axios failure into a typed, UI-friendly `ApiRequestError`. */
function toApiError(error: AxiosError<ApiErrorBody>): ApiRequestError {
  if (error.response) {
    const body = error.response.data;
    const message =
      (body && typeof body === "object" && "message" in body && body.message) ||
      error.message ||
      "Request failed";
    return new ApiRequestError(
      String(message),
      error.response.status,
      body?.details,
    );
  }
  if (error.request) {
    return new ApiRequestError(
      "Unable to reach the server. Check your connection and try again.",
      0,
      undefined,
      true,
    );
  }
  return new ApiRequestError(error.message || "Unexpected error", 0);
}

// ── Thin typed helpers that unwrap the success envelope ──────────────

export async function getData<T>(url: string, params?: object): Promise<T> {
  const res = await apiClient.get<ApiEnvelope<T>>(url, { params: clean(params) });
  return res.data.data;
}

/** GET a paginated list, returning both items and pagination metadata. */
export async function getList<T>(
  url: string,
  params?: object,
): Promise<{ items: T[]; meta: ApiEnvelope<T[]>["meta"] }> {
  const res = await apiClient.get<ApiEnvelope<T[]>>(url, { params: clean(params) });
  return { items: res.data.data, meta: res.data.meta };
}

export async function postData<T>(url: string, body?: unknown): Promise<T> {
  const res = await apiClient.post<ApiEnvelope<T>>(url, body);
  return res.data.data;
}

export async function patchData<T>(url: string, body?: unknown): Promise<T> {
  const res = await apiClient.patch<ApiEnvelope<T>>(url, body);
  return res.data.data;
}

export async function putData<T>(url: string, body?: unknown): Promise<T> {
  const res = await apiClient.put<ApiEnvelope<T>>(url, body);
  return res.data.data;
}

export async function deleteData<T>(url: string): Promise<T> {
  const res = await apiClient.delete<ApiEnvelope<T>>(url);
  return res.data.data;
}

/** Drop `undefined`/`null`/`""` params so they never hit the query string. */
function clean(params?: object): Record<string, unknown> | undefined {
  if (!params) return undefined;
  return Object.fromEntries(
    Object.entries(params as Record<string, unknown>).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  );
}
